import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Edit, MoreHorizontal, Trash2 } from '@/ui/icons'
import { ConfirmationDialog } from '@/ui/components'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/ui/components/primitives/dropdown-menu'
import type { RangerSessionRange, RangerSessionSummary } from '@/shared/api/ranger'
import { cn } from '@/shared/utils'
import {
  useDeleteRangerSession,
  useRangerSessions,
  useRenameRangerSession,
} from '../hooks/useRangerSessions'
import { formatChatTime } from '../utils/formatChatTime'
import { RangerMark } from './RangerMark'

const FILTERS: Array<{ range: RangerSessionRange; label: string }> = [
  { range: 'recent', label: 'Recent' },
  { range: 'previous_30_days', label: 'Previous 30 days' },
]

const EMPTY_MESSAGES: Record<RangerSessionRange, string> = {
  recent: 'Your chats with Ranger will show up here.',
  previous_30_days: 'No chats in the last 30 days.',
}

const CHAT_NAME_MAX_LENGTH = 100
const SKELETON_ROWS = 6

function sessionName(session: RangerSessionSummary): string {
  return session.title || 'Untitled chat'
}

function sessionDetails(session: RangerSessionSummary): string {
  const time = formatChatTime(session.lastActiveAt)
  return session.tripName ? `${time} · ${session.tripName}` : time
}

interface RangerHistoryProps {
  /** The chat currently open behind this list. */
  activeRuntimeSessionId: string
  openingSessionId: string | null
  openFailed: boolean
  onOpen: (session: RangerSessionSummary) => void
  onRenamed: (session: RangerSessionSummary, title: string) => void
  onDeleted: (session: RangerSessionSummary) => void
  onClose: () => void
}

/** The list of past chats, laid over the chat in the Ranger column. */
export function RangerHistory({
  activeRuntimeSessionId,
  openingSessionId,
  openFailed,
  onOpen,
  onRenamed,
  onDeleted,
  onClose,
}: RangerHistoryProps) {
  const [range, setRange] = useState<RangerSessionRange>('recent')
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<RangerSessionSummary | null>(null)
  const [mutationError, setMutationError] = useState<string | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const sessionsQuery = useRangerSessions(range)
  const renameSession = useRenameRangerSession()
  const deleteSession = useDeleteRangerSession()

  const sessions = sessionsQuery.data?.pages.flatMap((page) => page.sessions) ?? []

  // Takes focus so the keyboard starts here and not in the covered chat behind it.
  useEffect(() => {
    panelRef.current?.focus()
  }, [])

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // React bubbles through portals, so Escape in the delete dialog or a row's menu lands here too.
    if (event.key === 'Escape' && panelRef.current?.contains(event.target as Node)) onClose()
  }

  const commitRename = (session: RangerSessionSummary, title: string) => {
    setRenamingId(null)
    if (!title || title === session.title) return

    setMutationError(null)
    onRenamed(session, title)
    renameSession.mutate(
      { id: session.id, title },
      {
        onError: () => {
          onRenamed(session, session.title ?? '')
          setMutationError("Couldn't rename that chat. Try again.")
        },
      }
    )
  }

  const confirmDelete = () => {
    if (!deleting) return
    const session = deleting
    setDeleting(null)
    setMutationError(null)
    // Up front, like the list: a callback on the mutation wouldn't run if the user leaves history first.
    onDeleted(session)
    deleteSession.mutate(session.id, {
      onError: () => setMutationError("Couldn't delete that chat. Try again."),
    })
  }

  const notice = openFailed ? "Couldn't open that chat. Try again." : mutationError

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      className="absolute inset-0 flex flex-col bg-background outline-none duration-150 ease-out animate-in fade-in-0 slide-in-from-right-1 motion-reduce:animate-none"
    >
      <div role="group" aria-label="Filter chats" className="flex flex-shrink-0 gap-1 px-3 pb-2">
        {FILTERS.map((filter) => (
          <button
            key={filter.range}
            type="button"
            aria-pressed={range === filter.range}
            onClick={() => setRange(filter.range)}
            className={cn(
              'h-8 rounded-md px-2.5 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500',
              range === filter.range
                ? 'bg-earth-100 font-medium text-foreground'
                : 'text-earth-600 hover:text-foreground'
            )}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {notice && (
        <p role="alert" className="flex-shrink-0 px-4 pb-2 text-xs text-red-700 dark:text-red-300">
          {notice}
        </p>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {sessionsQuery.isPending ? (
          <div aria-hidden className="flex flex-col gap-0.5">
            {Array.from({ length: SKELETON_ROWS }, (_, i) => (
              <div key={i} className="rounded-lg px-2.5 py-2">
                <div className="h-5 w-3/4 animate-pulse rounded bg-earth-100 motion-reduce:animate-none" />
                <div className="mt-1 h-4 w-1/3 animate-pulse rounded bg-earth-100 motion-reduce:animate-none" />
              </div>
            ))}
          </div>
        ) : sessionsQuery.isError ? (
          <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
            <p className="text-sm text-earth-600">Couldn&apos;t load your chats.</p>
            <button
              type="button"
              onClick={() => sessionsQuery.refetch()}
              className="rounded-md px-2.5 py-1 text-sm font-medium text-foreground transition-colors hover:bg-earth-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500"
            >
              Try again
            </button>
          </div>
        ) : sessions.length === 0 ? (
          <p className="px-6 py-10 text-center text-sm text-earth-600">{EMPTY_MESSAGES[range]}</p>
        ) : (
          <>
            <ul className="flex flex-col gap-0.5">
              {sessions.map((session) => (
                <SessionRow
                  key={session.id}
                  session={session}
                  active={session.runtimeSessionId === activeRuntimeSessionId}
                  opening={session.id === openingSessionId}
                  renaming={session.id === renamingId}
                  onOpen={() => onOpen(session)}
                  onStartRename={() => setRenamingId(session.id)}
                  onCommitRename={(title) => commitRename(session, title)}
                  onCancelRename={() => setRenamingId(null)}
                  onDelete={() => setDeleting(session)}
                />
              ))}
            </ul>
            {sessionsQuery.hasNextPage && (
              <button
                type="button"
                onClick={() => sessionsQuery.fetchNextPage()}
                disabled={sessionsQuery.isFetchingNextPage}
                className="mt-1 w-full rounded-lg px-2.5 py-2 text-sm text-earth-600 transition-colors hover:bg-earth-100 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500 disabled:opacity-60"
              >
                {sessionsQuery.isFetchingNextPage ? 'Loading...' : 'Show more'}
              </button>
            )}
          </>
        )}
      </div>

      <ConfirmationDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null)
        }}
        title="Delete this chat?"
        description={deleting ? `"${sessionName(deleting)}" will be permanently deleted.` : ''}
        confirmText="Delete"
        variant="destructive"
        onConfirm={confirmDelete}
        onCloseAutoFocus={(event) => {
          // The row may be gone by now, so the list takes focus rather than the top of the page.
          event.preventDefault()
          panelRef.current?.focus()
        }}
      />
    </div>
  )
}

interface SessionRowProps {
  session: RangerSessionSummary
  active: boolean
  opening: boolean
  renaming: boolean
  onOpen: () => void
  onStartRename: () => void
  onCommitRename: (title: string) => void
  onCancelRename: () => void
  onDelete: () => void
}

function SessionRow({
  session,
  active,
  opening,
  renaming,
  onOpen,
  onStartRename,
  onCommitRename,
  onCancelRename,
  onDelete,
}: SessionRowProps) {
  // The menu hands focus back to its trigger as it closes, which would pull it out of the rename field.
  const keepFocusOnCloseRef = useRef(false)

  // Saving or cancelling removes the field. Without this, keyboard focus falls back to the top of the page.
  const rowButtonRef = useRef<HTMLButtonElement>(null)
  const wasRenamingRef = useRef(false)
  useEffect(() => {
    if (wasRenamingRef.current && !renaming && document.activeElement === document.body) {
      rowButtonRef.current?.focus()
    }
    wasRenamingRef.current = renaming
  }, [renaming])

  if (renaming) {
    return (
      <li className="rounded-lg bg-earth-100 px-2.5 py-2">
        <RenameInput session={session} onCommit={onCommitRename} onCancel={onCancelRename} />
        <span className="mt-0.5 block truncate text-xs text-earth-600">{sessionDetails(session)}</span>
      </li>
    )
  }

  return (
    <li className="group relative">
      <button
        ref={rowButtonRef}
        type="button"
        onClick={onOpen}
        aria-current={active ? 'true' : undefined}
        aria-busy={opening || undefined}
        className={cn(
          'block w-full rounded-lg py-2 pl-2.5 pr-9 text-left transition-colors hover:bg-earth-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-adventure-500',
          active && 'bg-earth-100'
        )}
      >
        <span className={cn('block truncate text-sm text-foreground', active && 'font-medium')}>
          {sessionName(session)}
        </span>
        <span className="mt-0.5 block truncate text-xs text-earth-600">{sessionDetails(session)}</span>
      </button>

      {opening ? (
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2">
          <RangerMark thinking className="h-4 w-4 text-adventure-600" />
          <span className="sr-only">Opening chat</span>
        </span>
      ) : (
        // Not modal: a modal menu opening the delete dialog can leave the page unclickable.
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`More options for ${sessionName(session)}`}
              className="absolute right-1.5 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-earth-600 opacity-0 transition-[opacity,background-color,color] hover:bg-earth-200 hover:text-foreground focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-adventure-500 group-hover:opacity-100 data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-36"
            onCloseAutoFocus={(event) => {
              if (keepFocusOnCloseRef.current) event.preventDefault()
              keepFocusOnCloseRef.current = false
            }}
          >
            <DropdownMenuItem
              onSelect={() => {
                keepFocusOnCloseRef.current = true
                onStartRename()
              }}
            >
              <Edit className="mr-2 h-4 w-4" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={onDelete}
              className="text-red-700 focus:text-red-700 dark:text-red-300 dark:focus:text-red-300"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </li>
  )
}

interface RenameInputProps {
  session: RangerSessionSummary
  onCommit: (title: string) => void
  onCancel: () => void
}

function RenameInput({ session, onCommit, onCancel }: RenameInputProps) {
  const [value, setValue] = useState(session.title ?? '')
  const inputRef = useRef<HTMLInputElement>(null)
  // Enter and Escape both remove the field, and removing it can fire a blur that would commit again.
  const settledRef = useRef(false)

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const commit = () => {
    if (settledRef.current) return
    settledRef.current = true
    onCommit(value.trim())
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      maxLength={CHAT_NAME_MAX_LENGTH}
      aria-label="Chat name"
      onChange={(event) => setValue(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          commit()
        } else if (event.key === 'Escape') {
          // Cancels the rename only, not the whole history list.
          event.stopPropagation()
          settledRef.current = true
          onCancel()
        }
      }}
      className="block h-5 w-full bg-transparent text-sm text-foreground outline-none"
    />
  )
}
